// Angular imports
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

// Third party imports
import { Subscription } from 'rxjs';

// devextreme imports
import notify from 'devextreme/ui/notify';

// cc-common imports
import { IconConstants, Utils } from 'cc-common';

// Constant imports
import { StringFormatConstants } from '../../../../_constants/string-format-constants';
import { MessageInterval } from '../../../../_constants/messages-intervals';

// Service imports
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { PrecisionService } from '../../../../_services/shared-services/precision.service';
import { DetailPopupsService } from '../../_services/detail-popups.service';
import { APCreditApplicationsService } from '../../_services/accounts-payable/ap-credit-applications.service';
import { HelperService } from '../../../../_services/shared-services/helper.service';

// Model imports
import { APBillParameterDto } from '../../_models/accounts-payable/ap-bill/ap-bill-parameter-dto';
import { APPaymentParameterDto } from '../../_models/accounts-payable/ap-payment/ap-payment-parameter-dto';
import { APAdjustmentParameterDto } from '../../_models/accounts-payable/ap-adjustment/ap-adjustment-parameter-dto';
import { APCreditApplicationResultDto } from '../../_models/accounts-payable/ap-credit-application/ap-credit-application-result-dto';
import { APCreditApplicationUnapplyParametersDto } from '../../_models/accounts-payable/ap-credit-application/ap-credit-application-unapply-parameter-dto';

@Component({
    selector: 'app-ap-credit-application',
    templateUrl: './ap-credit-application.component.html',
    styleUrls: ['./ap-credit-application.component.scss']
})
export class APCreditApplicationComponent implements OnInit, OnDestroy {
    @Input() visible = false;
    @Input() creditApplicationData: APCreditApplicationResultDto[] = [];
    @Input() transactionType: string;
    @Input() transactionId: number;
    @Input() communityId: number;
    @Input() managementCompanyId: number;
    @Input() popupTitle: string;
    @Output() onCreditApplicationClose = new EventEmitter<boolean>();

    loadingVisible = false;
    popUpTitle: string;
    currencyFormat: any;
    apPaymentDetailVisible = false;
    apAdjustmentDetailVisible = false;
    apBillDetailVisible = false;

    apPaymentParams = new APPaymentParameterDto();
    apAdjustmentDetailParam = new APAdjustmentParameterDto();
    apBillDetailParam = new APBillParameterDto();

    dateFormat = StringFormatConstants.dateFormat;

    displayPaymentDetails = false;

    iconClose = IconConstants.CLOSE;

    forceRefresh = false;
    forceReloadSubscription: Subscription;

    constructor(
        private readonly apCreditApplicationService: APCreditApplicationsService,
        private readonly errorService: ServerErrorService,
        private readonly precisionService: PrecisionService,
        private readonly detailPopupsService: DetailPopupsService,
        private readonly helperService: HelperService
    ) {
        this.currencyFormat = this.precisionService.getCurrencyFormat();
        this.calculateCustomSummary = this.calculateCustomSummary.bind(this);
    }

    ngOnInit() {
        this.forceReloadSubscription = this.detailPopupsService.onForceReload().subscribe(() => {
            this.forceRefresh = true;
        });
    }

    ngOnDestroy() {
        if (Utils.notNullAndDefined(this.forceReloadSubscription)) {
            this.forceReloadSubscription.unsubscribe();
        }
    }

    onHidden(): void {
        this.closeCreditApplication();
    }

    onSourceClick(data: APCreditApplicationResultDto): void {
        if (Utils.equalsIgnoreCase(data.source.type, 'P')) {
            this.apPaymentParams.managementCompanyId = this.managementCompanyId;
            this.apPaymentParams.communityId = this.communityId;
            this.apPaymentParams.paymentId = data.source.id;
            this.apPaymentParams.clearCache = false;
            this.apPaymentDetailVisible = true;
        } else if (Utils.equalsIgnoreCase(data.source.type, 'A')) {
            this.apAdjustmentDetailParam.managementCompanyId = this.managementCompanyId;
            this.apAdjustmentDetailParam.communityId = this.communityId;
            this.apAdjustmentDetailParam.id = data.source.id;
            this.apAdjustmentDetailParam.clearCache = false;
            this.apAdjustmentDetailVisible = true;
        } else if (Utils.equalsIgnoreCase(data.source.type, 'B')) {
            this.apBillDetailParam.managementCompanyId = this.managementCompanyId;
            this.apBillDetailParam.communityId = this.communityId;
            this.apBillDetailParam.billId = data.source.id;
            this.apBillDetailParam.clearCache = false;
            this.apBillDetailVisible = true;
        } else {
            this.detailPopupsService.showDetailsPopup('AP', data.source.type, {
                managementCompanyId: this.managementCompanyId,
                communityId: this.communityId,
                headerId: data.source.id
            });
        }
    }

    onAPPaymentDetailClose(e: any): void {
        this.apPaymentDetailVisible = false;
        this.apPaymentParams = new APPaymentParameterDto();
        if (Utils.isTrue(e)) {
            this.detailPopupsService.updateForceReload(e);
        }
    }

    onAPAdjustmentDetailClose(e: any): void {
        this.apAdjustmentDetailVisible = false;
        this.apAdjustmentDetailParam = new APAdjustmentParameterDto();
        if (Utils.isTrue(e)) {
            this.detailPopupsService.updateForceReload(e);
        }
    }

    onAPBillDetailClose(e: any): void {
        this.apBillDetailVisible = false;
        this.apBillDetailParam = new APBillParameterDto();
        if (Utils.isTrue(e)) {
            this.detailPopupsService.updateForceReload(e);
        }
    }

    onTargetClick(data: APCreditApplicationResultDto): void {
        if (Utils.equalsIgnoreCase(data.target.type, 'P')) {
            this.apPaymentParams.managementCompanyId = this.managementCompanyId;
            this.apPaymentParams.communityId = this.communityId;
            this.apPaymentParams.paymentId = data.target.id;
            this.apPaymentParams.clearCache = false;
            this.apPaymentDetailVisible = true;
        } else if (Utils.equalsIgnoreCase(data.target.type, 'A')) {
            this.apAdjustmentDetailParam.managementCompanyId = this.managementCompanyId;
            this.apAdjustmentDetailParam.communityId = this.communityId;
            this.apAdjustmentDetailParam.id = data.target.id;
            this.apAdjustmentDetailParam.clearCache = false;
            this.apAdjustmentDetailVisible = true;
        } else if (Utils.equalsIgnoreCase(data.target.type, 'B')) {
            this.apBillDetailParam.managementCompanyId = this.managementCompanyId;
            this.apBillDetailParam.communityId = this.communityId;
            this.apBillDetailParam.billId = data.target.id;
            this.apBillDetailParam.clearCache = false;
            this.apBillDetailVisible = true;
        } else {
            this.detailPopupsService.showDetailsPopup('AP', data.target.type, {
                managementCompanyId: this.managementCompanyId,
                communityId: this.communityId,
                headerId: data.target.id
            });
        }
    }

    onUnapplyClick(data: APCreditApplicationResultDto): void {
        const param: APCreditApplicationUnapplyParametersDto = new APCreditApplicationUnapplyParametersDto();
        param.communityId = this.communityId;
        param.managementCompanyId = this.managementCompanyId;
        param.creditApplicationId = data.creditApplicationId;
        this.loadingVisible = true;
        this.apCreditApplicationService.unapplyAPCreditApplication(param).subscribe(
            () => {
                notify('Credit application unapplied successfully', 'success', MessageInterval.successMsgInterval);
                this.forceRefresh = true;
                this.closeCreditApplication();
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    calculateCustomSummary(e: any): void {
        this.helperService.calculateTotalSummary(e);
    }

    private closeCreditApplication(): void {
        this.visible = false;
        this.onCreditApplicationClose.emit(this.forceRefresh);
    }
}
